import { useEffect, useRef, useState } from 'react';
import type { ApolloError } from '@apollo/client';
import { useGetTriggerEmailRunsQuery, type GetTriggerEmailRunsQuery } from '~/api/graphql';

const accessCodes = new Set(['UNAUTHORIZED', 'UNAUTHENTICATED', 'FORBIDDEN', 'NOT_FOUND']);

/** Access loss must discard previously readable rows, including partial responses. */
const lostAccess = (error?: ApolloError) => {
  const network = error?.networkError;
  if (network && 'statusCode' in network && [401, 403, 404].includes(Number(network.statusCode))) {
    return true;
  }
  return (
    error?.graphQLErrors.some((item) => {
      const application = item.extensions?.applicationError as { code?: unknown } | undefined;
      return (
        accessCodes.has(String(item.extensions?.code)) || accessCodes.has(String(application?.code))
      );
    }) ?? false
  );
};

type Runs = GetTriggerEmailRunsQuery['triggerEmailRuns'];

/** Mounted once per trigger and principal scope; never a cross-view last-good cache. */
export const useRecentEmailRuns = (triggerId: string, enabled: boolean) => {
  const [lastGood, setLastGood] = useState<Runs>();
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const retryPending = useRef(false);
  const query = useGetTriggerEmailRunsQuery({
    variables: { triggerDefinitionId: triggerId, first: 10 },
    skip: !enabled,
    pollInterval: 5000,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    // A new principal must not join an old principal's in-flight request, even
    // when the operation variables happen to be identical.
    context: { queryDeduplication: false },
    onCompleted: (data) => {
      // Apollo 3 calls this on network completion, not ready-state cache
      // broadcasts. Never promote another view's late cache write to last-good.
      const runs = data.triggerEmailRuns;
      if (
        enabled &&
        Array.isArray(runs) &&
        runs.every((run) => run.triggerDefinitionId === triggerId)
      ) {
        setLastGood(runs);
        setRetryFailed(false);
      } else {
        setRetryFailed(true);
      }
    }
  });
  const revoked = lostAccess(query.error);
  useEffect(() => {
    if (revoked || !enabled) setLastGood(undefined);
    // Apollo may clear query.error as the next poll starts. Keep the warning
    // until that read succeeds; an old empty snapshot is not recovery.
    if (query.error) setRetryFailed(true);
  }, [revoked, enabled, query.error]);

  const runs = revoked || !enabled ? undefined : lastGood;
  const failed =
    !!query.error || retryFailed || retrying || (enabled && !query.loading && !query.data);
  const retry = async () => {
    if (!enabled || query.loading || retryPending.current) return;
    retryPending.current = true;
    setRetrying(true);
    setRetryFailed(false);
    try {
      await query.refetch();
    } catch {
      // Apollo normally exposes the failure on query.error; rejected transports
      // still need generic feedback, never their diagnostic message.
      setRetryFailed(true);
    } finally {
      retryPending.current = false;
      setRetrying(false);
    }
  };

  return { runs, failed, pending: query.loading || retrying, retry };
};
